import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import api, { projectAPI, eventAPI, crewAPI, assignmentAPI, notificationAPI, driveAPI } from '../utils/api';
import { formatDate, formatCurrency } from '../utils/helpers';
import { Plus, Edit, Trash2, Calendar as CalendarIcon, Users as UsersIcon, ChevronRight, Send, Mail, MessageSquare, Phone, X, Archive, ArchiveRestore, Upload, FileText, Image, Film, File as FileIcon, ExternalLink, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { toast } from 'sonner';
import ProjectTimeline from '../components/ProjectTimeline';
import VendorsSection from '../components/VendorsSection';
import ProjectNotes from '../components/ProjectNotes';
import ProjectFinancePanel from '../components/ProjectFinancePanel';
import EditAmountModal from '../components/EditAmountModal';
import ShotListAdmin from '../components/ShotListAdmin';
import { Camera as CameraIcon } from 'lucide-react';

const Projects = () => {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectEvents, setProjectEvents] = useState([]);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [amountEditFor, setAmountEditFor] = useState(null);
  const [amountEditEventId, setAmountEditEventId] = useState(null);
  const [showProjectAssignModal, setShowProjectAssignModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState(null);
  const [selectedAssignmentsForNotification, setSelectedAssignmentsForNotification] = useState([]);
  const [notificationEvent, setNotificationEvent] = useState(null);
  const [sendingNotification, setSendingNotification] = useState(false);
  const [assignmentType, setAssignmentType] = useState('event'); // 'event' or 'project'
  const [editingProject, setEditingProject] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [crewMembers, setCrewMembers] = useState([]);
  const [projectFormData, setProjectFormData] = useState({});
  const [eventFormData, setEventFormData] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [eventAssignments, setEventAssignments] = useState({});
  const [availability, setAvailability] = useState({});
  const [pendingConflicts, setPendingConflicts] = useState(null);
  const [pendingSubmitPayload, setPendingSubmitPayload] = useState(null);
  const [projectTab, setProjectTab] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [projectEventView, setProjectEventView] = useState('list');
  const [timeChangeInfo, setTimeChangeInfo] = useState(null);
  const [projectFiles, setProjectFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedYear, setSelectedYear] = useState('all');
  const [collapsedYears, setCollapsedYears] = useState({});

  // Group projects by year based on project_start_date
  const getProjectYear = (project) => {
    if (!project.project_start_date) return 'No Date';
    return new Date(project.project_start_date).getFullYear().toString();
  };

  // Year sort priority:
  //  1. Current year (today's year) first
  //  2. Future years next, ascending (next year, year after, ...)
  //  3. Past years last, descending (most recent past first)
  //  4. "No Date" always at the bottom
  const currentYear = new Date().getFullYear();
  const yearSortKey = (y) => {
    if (y === 'No Date') return [3, 0];
    const n = Number(y);
    if (n === currentYear) return [0, 0];
    if (n > currentYear) return [1, n];       // future: ascending
    return [2, -n];                            // past: descending
  };
  const compareYears = (a, b) => {
    const [ga, ka] = yearSortKey(a);
    const [gb, kb] = yearSortKey(b);
    if (ga !== gb) return ga - gb;
    return ka - kb;
  };

  // Within a year, sort by "upcoming/in-progress first, then most-recently-past".
  // "Active" (top): project_end_date >= today → sorted by start_date ascending (next wedding at top)
  // "Past" (bottom): project_end_date < today → sorted by end_date descending (most recent past first)
  // "No date" (last)
  const todayISO = new Date().toISOString().slice(0, 10);
  const compareProjectsByDate = (a, b) => {
    const aStart = a.project_start_date || '';
    const aEnd = a.project_end_date || aStart;
    const bStart = b.project_start_date || '';
    const bEnd = b.project_end_date || bStart;

    if (!aStart && !bStart) return 0;
    if (!aStart) return 1;
    if (!bStart) return -1;

    const aActive = aEnd >= todayISO;
    const bActive = bEnd >= todayISO;
    if (aActive && !bActive) return -1;
    if (!aActive && bActive) return 1;
    if (aActive) {
      // Both upcoming/in-progress → soonest start first
      return aStart.localeCompare(bStart);
    }
    // Both past → most recently ended first
    return bEnd.localeCompare(aEnd);
  };

  const getAvailableYears = () => {
    const years = [...new Set(projects.map(getProjectYear))].filter(y => y !== 'No Date');
    years.sort(compareYears);
    if (projects.some(p => !p.project_start_date)) {
      years.push('No Date');
    }
    return years;
  };

  const filteredByYear = selectedYear === 'all' 
    ? projects 
    : projects.filter(p => getProjectYear(p) === selectedYear);

  const searchTerm = searchQuery.trim().toLowerCase();
  const filteredBySearch = !searchTerm
    ? filteredByYear
    : filteredByYear.filter(p => {
        const haystack = [
          p.project_name,
          p.bride_name,
          p.groom_name,
          p.bride_instagram,
          p.groom_instagram,
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(searchTerm);
      });

  const projectsByYear = filteredBySearch.reduce((acc, project) => {
    const year = getProjectYear(project);
    if (!acc[year]) acc[year] = [];
    acc[year].push(project);
    return acc;
  }, {});

  // Sort projects inside each year by upcoming-first
  Object.keys(projectsByYear).forEach(y => {
    projectsByYear[y].sort(compareProjectsByDate);
  });

  const sortedYears = Object.keys(projectsByYear).sort(compareYears);

  // Flat, globally-sorted list (used by project switcher dropdown)
  const globallySortedProjects = [...projects].sort((a, b) => {
    const yc = compareYears(getProjectYear(a), getProjectYear(b));
    if (yc !== 0) return yc;
    return compareProjectsByDate(a, b);
  });

  // Urgency badge: days until project_start_date determines color/label
  const getUrgency = (project) => {
    if (!project.project_start_date) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(project.project_start_date + 'T00:00:00');
    const diffDays = Math.round((eventDate - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return null;                       // past — no badge
    if (diffDays === 0) return { label: 'TODAY', color: 'red' };
    if (diffDays <= 7) return { label: `${diffDays}D · URGENT`, color: 'red' };
    if (diffDays <= 30) return { label: `${diffDays}D · UPCOMING`, color: 'gold' };
    return null;
  };

  const toggleYearCollapse = (year) => {
    setCollapsedYears(prev => ({ ...prev, [year]: !prev[year] }));
  };

  const fetchProjects = useCallback(async (status) => {
    try {
      const response = await projectAPI.getAll(status || projectTab);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, [projectTab]);

  const fetchCrew = useCallback(async () => {
    try {
      const response = await crewAPI.getAll();
      setCrewMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch crew:', error);
    }
  }, []);

  useEffect(() => {
    fetchProjects(projectTab);
    if (isAdmin) {
      fetchCrew();
    }
  }, [projectTab, isAdmin, fetchProjects, fetchCrew]);

  // Deep link: /projects?project_id=X&assign_event_id=Y → auto-open that project + assignment modal
  useEffect(() => {
    const projectId = searchParams.get('project_id');
    const assignEventId = searchParams.get('assign_event_id');
    if (!projectId || projects.length === 0) return;

    const project = projects.find(p => p.project_id === projectId);
    if (!project) return;

    // Select the project and load its events
    (async () => {
      setSelectedProject(project);
      const eventsRes = await eventAPI.getByProject(projectId);
      setProjectEvents(eventsRes.data);

      if (assignEventId) {
        const event = eventsRes.data.find(e => e.event_id === assignEventId);
        if (event) {
          setSelectedEvent(event);
          setAssignmentType('event');
          setAssignments([{
            crew_member_id: '',
            day_type: 'full_day',
            currency: 'USD',
            custom_amount_usd: 0,
            custom_amount_inr: 0,
            assigned_gear: []
          }]);
          setShowAssignModal(true);
        }
      }
      // Clear query params so refresh doesn't re-open modal
      setSearchParams({}, { replace: true });
    })();
  }, [searchParams, projects, setSearchParams]);

  const fetchNotificationConfig = async () => {
    try {
      const response = await notificationAPI.getConfig();
      setNotificationConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch notification config:', error);
    }
  };

  const openNotificationModal = (event, assignments) => {
    setNotificationEvent(event);
    setSelectedAssignmentsForNotification(assignments.map(a => a.assignment_id));
    fetchNotificationConfig();
    setShowNotificationModal(true);
  };

  const sendNotification = async (notificationType) => {
    if (selectedAssignmentsForNotification.length === 0) {
      toast.error('No crew members selected');
      return;
    }

    setSendingNotification(true);
    try {
      const response = await notificationAPI.send(
        selectedAssignmentsForNotification,
        notificationType
      );
      
      const { results } = response.data;
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success);
      
      if (successful > 0) {
        toast.success(`${successful} notification(s) sent successfully!`);
      }
      
      if (failed.length > 0) {
        failed.forEach(f => {
          toast.error(`Failed to notify ${f.crew_name}: ${f.error}`);
        });
      }
      
      setShowNotificationModal(false);
    } catch (error) {
      toast.error('Failed to send notifications');
      console.error(error);
    } finally {
      setSendingNotification(false);
    }
  };

  const removeCrewAssignment = async (assignmentId, eventId) => {
    if (!confirm('Are you sure you want to remove this crew member from the event?')) {
      return;
    }
    try {
      await api.delete(`/api/assignments/${assignmentId}`);
      toast.success('Crew member removed from event');
      // Refresh assignments for this event
      const assignRes = await eventAPI.getAssignments(eventId);
      setEventAssignments(prev => ({
        ...prev,
        [eventId]: assignRes.data
      }));
    } catch (error) {
      toast.error('Failed to remove crew member');
      console.error(error);
    }
  };

  const fetchProjectEvents = async (projectId) => {
    try {
      const response = await eventAPI.getByProject(projectId);
      setProjectEvents(response.data);
      
      // Fetch assignments for each event
      const assignmentsMap = {};
      for (const event of response.data) {
        try {
          const assignRes = await eventAPI.getAssignments(event.event_id);
          assignmentsMap[event.event_id] = assignRes.data;
          console.log(`Assignments for ${event.event_name}:`, assignRes.data);
        } catch (err) {
          console.error(`Failed to fetch assignments for event ${event.event_id}:`, err);
          assignmentsMap[event.event_id] = [];
        }
      }
      console.log('All assignments map:', assignmentsMap);
      setEventAssignments(assignmentsMap);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const handleCreateProject = () => {
    setEditingProject(null);
    setProjectFormData({
      project_name: '',
      client_name: '',
      bride_name: '',
      groom_name: '',
      bride_instagram: '',
      groom_instagram: '',
      client_email: '',
      client_phone: '',
      secondary_contact_name: '',
      secondary_contact_phone: '',
      project_start_date: '',
      project_end_date: '',
      notes: ''
    });
    setShowProjectModal(true);
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setProjectFormData(project);
    setShowProjectModal(true);
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        // Strip vendors from payload — they're managed separately via VendorsSection
        // and could be stale in the edit form snapshot
        const { vendors: _vendors, ...payload } = projectFormData;
        const response = await projectAPI.update(editingProject.project_id, payload);
        // If we're in detail view, also refresh selectedProject (preserving current vendors)
        if (selectedProject && selectedProject.project_id === editingProject.project_id) {
          setSelectedProject(prev => ({ ...response.data, vendors: prev?.vendors ?? response.data.vendors }));
        }
      } else {
        await projectAPI.create(projectFormData);
      }
      setShowProjectModal(false);
      fetchProjects();
    } catch (error) {
      alert('Failed to save project');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (window.confirm('Are you sure? This will permanently delete the project, all its events, and all payment records.')) {
      try {
        await projectAPI.delete(projectId);
        toast.success('Project deleted');
        fetchProjects();
      } catch (error) {
        toast.error('Failed to delete project');
      }
    }
  };

  const handleArchiveProject = async (projectId) => {
    try {
      await projectAPI.archive(projectId);
      toast.success('Project archived');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to archive project');
    }
  };

  const handleUnarchiveProject = async (projectId) => {
    try {
      await projectAPI.unarchive(projectId);
      toast.success('Project restored');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to restore project');
    }
  };

  const fetchProjectFiles = async (projectId) => {
    try {
      const res = await driveAPI.listFiles(projectId);
      setProjectFiles(res.data);
    } catch {
      setProjectFiles([]);
    }
  };

  const handleFileUpload = async (e, projectId) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('project_id', projectId);
      await driveAPI.upload(formData);
      toast.success('File uploaded to Google Drive');
      fetchProjectFiles(projectId);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Upload failed';
      if (detail.includes('not connected')) {
        toast.error('Connect Google Drive in Settings first');
      } else {
        toast.error(detail);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId, projectId) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await driveAPI.deleteFile(fileId);
      toast.success('File deleted');
      fetchProjectFiles(projectId);
    } catch {
      toast.error('Failed to delete file');
    }
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return FileIcon;
    if (mimeType.startsWith('image/')) return Image;
    if (mimeType.startsWith('video/')) return Film;
    if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('text')) return FileText;
    return FileIcon;
  };

  const handleViewProject = async (project) => {
    setSelectedProject(project);
    await Promise.all([
      fetchProjectEvents(project.project_id),
      fetchProjectFiles(project.project_id)
    ]);
  };

  const handleCreateEvent = () => {
    if (!selectedProject) return;
    setEditingEvent(null);
    setEventFormData({
      event_name: '',
      event_type: 'Wedding',
      event_date: '',
      start_time: '09:00 AM',
      end_time: '05:00 PM',
      location: '',
      venue_address: '',
      event_notes: '',
      required_photographers: 0,
      required_videographers: 0,
      required_cinematographers: 0
    });
    setShowEventModal(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventFormData({
      ...event,
      required_photographers: event.required_photographers || 0,
      required_videographers: event.required_videographers || 0,
      required_cinematographers: event.required_cinematographers || 0,
    });
    setShowEventModal(true);
  };

  const handleEventSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (editingEvent) {
        response = await eventAPI.update(editingEvent.event_id, eventFormData);
      } else {
        response = await eventAPI.create(selectedProject.project_id, eventFormData);
      }
      setShowEventModal(false);
      fetchProjectEvents(selectedProject.project_id);

      // Handle time-change auto-notify prompt
      const meta = response?.data?._meta;
      if (editingEvent && meta?.time_changed && meta.assigned_crew_count > 0) {
        setTimeChangeInfo({
          event_id: editingEvent.event_id,
          event_name: editingEvent.event_name,
          old_values: meta.old_values,
          new_values: meta.new_values,
          crew_count: meta.assigned_crew_count,
          google_synced: meta.google_synced,
        });
      } else if (editingEvent && meta?.time_changed && meta.google_synced) {
        toast.success('Event updated & Google Calendar re-synced');
      } else if (editingEvent) {
        toast.success('Event updated');
      }
    } catch (error) {
      alert('Failed to save event');
    }
  };

  const sendTimeChangeNotifications = async (channels) => {
    if (!timeChangeInfo) return;
    try {
      const res = await api.post(`/api/events/${timeChangeInfo.event_id}/notify-time-change`, {
        old_event_date: timeChangeInfo.old_values?.event_date,
        old_start_time: timeChangeInfo.old_values?.start_time,
        old_end_time: timeChangeInfo.old_values?.end_time,
        channels,
      });
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Failed to send notifications');
    } finally {
      setTimeChangeInfo(null);
    }
  };

  const handleDeleteEvent = async (eventId, e) => {
    if (e) e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this event and all its assignments?')) {
      try {
        await eventAPI.delete(eventId);
        toast.success('Event deleted');
        fetchProjectEvents(selectedProject.project_id);
      } catch (error) {
        console.error('Delete failed:', error);
        toast.error('Failed to delete event');
      }
    }
  };

  const fetchAvailabilityForDate = async (date, excludeEventId) => {
    if (!date) {
      setAvailability({});
      return;
    }
    try {
      const params = new URLSearchParams({ date });
      if (excludeEventId) params.append('exclude_event_id', excludeEventId);
      const resp = await api.get(`/api/crew-availability?${params.toString()}`);
      const map = {};
      (resp.data || []).forEach(c => { map[c.crew_id] = c; });
      setAvailability(map);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      setAvailability({});
    }
  };

  const handleAssignCrew = (event) => {
    setSelectedEvent(event);
    setAssignmentType('event');
    setAssignments([{
      crew_member_id: '',
      day_type: 'full_day',
      currency: 'USD',
      custom_amount_usd: 0,
      custom_amount_inr: 0,
      assigned_gear: []
    }]);
    setShowAssignModal(true);
    fetchAvailabilityForDate(event?.event_date, event?.event_id);
  };

  const handleAssignCrewToProject = () => {
    setAssignmentType('project');
    setAssignments([{
      crew_member_id: '',
      day_type: 'full_day',
      currency: 'USD',
      custom_amount_usd: 0,
      custom_amount_inr: 0,
      assigned_gear: []
    }]);
    setShowProjectAssignModal(true);
  };

  const performAssignmentSave = async (payload) => {
    try {
      if (payload.type === 'event') {
        await assignmentAPI.create({
          event_id: selectedEvent.event_id,
          crew_assignments: payload.assignments
        });
        setShowAssignModal(false);
        toast.success('Crew assigned to event successfully');
        await fetchProjectEvents(selectedProject.project_id);
      } else {
        const response = await api.post(`/api/projects/${selectedProject.project_id}/assign-crew`, {
          crew_assignments: payload.assignments
        });
        setShowProjectAssignModal(false);
        toast.success(`Crew assigned to all ${response.data.events_count} events`);
        await fetchProjectEvents(selectedProject.project_id);
      }
    } catch (error) {
      console.error('Assignment failed:', error);
      toast.error('Failed to assign crew');
    } finally {
      setPendingConflicts(null);
      setPendingSubmitPayload(null);
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    const cleanAssignments = assignments.filter(a => a.crew_member_id);
    if (cleanAssignments.length === 0) {
      toast.error('Please select at least one crew member');
      return;
    }

    const payload = { type: assignmentType, assignments: cleanAssignments };

    // For single-event assignment, run the conflict check
    if (assignmentType === 'event' && selectedEvent?.event_id) {
      try {
        const crewIds = cleanAssignments.map(a => a.crew_member_id).join(',');
        const resp = await api.get(
          `/api/assignments/check-conflicts?event_id=${selectedEvent.event_id}&crew_member_ids=${encodeURIComponent(crewIds)}`
        );
        const withIssues = (resp.data || []).filter(c => c.blocked || (c.conflicts || []).length > 0);
        if (withIssues.length > 0) {
          setPendingConflicts(withIssues);
          setPendingSubmitPayload(payload);
          return; // Wait for user confirmation in modal
        }
      } catch (error) {
        console.error('Conflict check failed, proceeding:', error);
      }
    }

    await performAssignmentSave(payload);
  };

  if (selectedProject) {
    return (
      <Layout>
        <div className="p-4 sm:p-6 lg:p-8" data-testid="project-details-view">
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center gap-3 mb-3 sm:mb-4 flex-wrap">
              <button
                onClick={() => setSelectedProject(null)}
                className="text-sm text-muted-foreground hover:text-foreground"
                data-testid="back-to-projects"
              >
                ← Back
              </button>
              <span className="text-muted-foreground">/</span>
              <select
                value={selectedProject.project_id}
                onChange={(e) => {
                  const p = projects.find(pr => pr.project_id === e.target.value);
                  if (p) handleViewProject(p);
                }}
                className="px-2 py-1 text-sm bg-background border border-border hover:border-gold focus:border-gold focus:outline-none max-w-[240px] sm:max-w-[360px] truncate"
                data-testid="project-switcher"
              >
                {globallySortedProjects.map(p => (
                  <option key={p.project_id} value={p.project_id}>
                    {p.bride_name && p.groom_name
                      ? `${p.bride_name} & ${p.groom_name}`
                      : p.project_name}
                  </option>
                ))}
              </select>
              <span className="text-xs text-muted-foreground hidden sm:inline">
                Switch project
              </span>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-4xl font-serif mb-1 sm:mb-2">
                  {selectedProject.bride_name && selectedProject.groom_name 
                    ? `${selectedProject.bride_name} & ${selectedProject.groom_name}`
                    : selectedProject.project_name}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {selectedProject.project_name}
                  {selectedProject.project_start_date && ` • ${formatDate(selectedProject.project_start_date)}`}
                </p>
              </div>
              {isAdmin && (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleAssignCrewToProject}
                    className="flex items-center gap-1.5 px-3 sm:px-4 py-2 border border-black/15 text-foreground hover:bg-black hover:text-white transition-colors text-xs"
                    data-testid="assign-crew-to-project"
                  >
                    <UsersIcon size={14} />
                    <span className="hidden sm:inline">ASSIGN </span>CREW ALL
                  </button>
                  <button
                    onClick={() => handleEditProject(selectedProject)}
                    className="px-3 sm:px-4 py-2 border border-border hover:border-gold transition-colors text-xs"
                  >
                    EDIT
                  </button>
                  {(selectedProject.status || 'active') === 'active' ? (
                    <button
                      onClick={() => { handleArchiveProject(selectedProject.project_id); setSelectedProject(null); }}
                      className="flex items-center gap-1.5 px-3 sm:px-4 py-2 border border-border hover:border-gold transition-colors text-xs"
                      data-testid="archive-project-detail"
                    >
                      <Archive size={14} />
                      ARCHIVE
                    </button>
                  ) : (
                    <button
                      onClick={() => { handleUnarchiveProject(selectedProject.project_id); setSelectedProject(null); }}
                      className="flex items-center gap-2 px-4 py-2 border border-green-300 text-green-600 dark:text-green-400 hover:bg-green-50 dark:bg-green-500/10 transition-colors text-xs"
                      data-testid="unarchive-project-detail"
                    >
                      <ArchiveRestore size={14} />
                      RESTORE
                    </button>
                  )}
                  <button
                    onClick={handleCreateEvent}
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 hover:bg-primary/90"
                  >
                    <Plus size={16} />
                    ADD EVENT
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Contact</p>
              <p className="text-sm">{selectedProject.client_email}</p>
              <p className="text-sm">{selectedProject.client_phone}</p>
            </div>
            <div className="card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Project Duration</p>
              <p className="text-sm">{formatDate(selectedProject.project_start_date)} - {formatDate(selectedProject.project_end_date)}</p>
            </div>
            <div className="card p-4">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Total Events</p>
              <p className="text-2xl font-serif">{projectEvents.length}</p>
            </div>
          </div>

          {isAdmin && (
            <div className="mb-6">
              <VendorsSection
                project={selectedProject}
                onProjectUpdated={(updated) => {
                  setSelectedProject(updated);
                  // Also refresh the list so changes persist when going back
                  setProjects(prev => prev.map(p => p.project_id === updated.project_id ? { ...p, ...updated } : p));
                }}
              />
            </div>
          )}

          {isAdmin && (
            <div className="mb-6">
              <ProjectFinancePanel projectId={selectedProject.project_id} />
            </div>
          )}

          {isAdmin && (
            <div className="mb-6">
              <ProjectNotes projectId={selectedProject.project_id} currentUserId={user?.user_id} />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <h2 className="text-2xl font-serif">Events</h2>
              <div className="flex gap-1">
                <button
                  onClick={() => setProjectEventView('list')}
                  className={`px-3 py-1.5 text-xs transition-colors ${
                    projectEventView === 'list'
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-border hover:bg-accent'
                  }`}
                  data-testid="project-view-list"
                >
                  LIST
                </button>
                <button
                  onClick={() => setProjectEventView('timeline')}
                  className={`px-3 py-1.5 text-xs transition-colors ${
                    projectEventView === 'timeline'
                      ? 'bg-primary text-primary-foreground'
                      : 'border border-border hover:bg-accent'
                  }`}
                  data-testid="project-view-timeline"
                >
                  TIMELINE
                </button>
              </div>
            </div>
            {projectEvents.length > 0 && projectEventView === 'timeline' ? (
              <ProjectTimeline
                events={projectEvents}
                eventAssignments={eventAssignments}
                isAdmin={isAdmin}
                onAssignCrew={handleAssignCrew}
                onEditEvent={handleEditEvent}
              />
            ) : projectEvents.length > 0 ? (
              <div className="space-y-4">
                {projectEvents.map((event) => (
                  <div key={event.event_id} className="card p-6" data-testid={`event-${event.event_id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-medium">{event.event_name}</h3>
                        <span className="inline-block mt-2 px-3 py-1 bg-gold/10 text-gold text-sm">
                          {event.event_type}
                        </span>
                      </div>
                      {isAdmin && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAssignCrew(event)}
                            className="p-2 border border-border hover:border-gold transition-colors"
                            title="Assign Crew"
                            data-testid={`assign-crew-event-${event.event_id}`}
                          >
                            <UsersIcon size={16} />
                          </button>
                          <button
                            onClick={() => handleEditEvent(event)}
                            className="p-2 border border-border hover:border-gold transition-colors"
                            data-testid={`edit-event-${event.event_id}`}
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={(e) => handleDeleteEvent(event.event_id, e)}
                            className="p-2 border border-destructive text-destructive hover:bg-destructive/10"
                            data-testid={`delete-event-${event.event_id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Date</p>
                        <p>{formatDate(event.event_date)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Time</p>
                        <p>{event.start_time} - {event.end_time}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Location</p>
                        <p>{event.location}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Venue</p>
                        <p>{event.venue_address}</p>
                      </div>
                    </div>

                    {/* CREW ASSIGNMENTS SECTION */}
                    <div className="mt-4 pt-4 border-t border-border">
                      {eventAssignments[event.event_id] ? (
                        eventAssignments[event.event_id].length > 0 ? (
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <p className="text-sm font-medium">
                                Assigned Crew ({eventAssignments[event.event_id].length})
                              </p>
                              {isAdmin && (
                                <button
                                  onClick={() => openNotificationModal(event, eventAssignments[event.event_id])}
                                  className="flex items-center gap-2 px-3 py-1 text-xs border border-black/15 text-foreground hover:bg-black hover:text-white transition-colors"
                                  data-testid={`notify-crew-${event.event_id}`}
                                >
                                  <Send size={12} />
                                  NOTIFY CREW
                                </button>
                              )}
                            </div>
                            <div className="space-y-2">
                              {eventAssignments[event.event_id].map((assignment) => {
                                const confirmStatus = assignment.confirmation_status || 'pending_confirmation';
                                return (
                                <div 
                                  key={assignment.assignment_id}
                                  className={`flex items-center justify-between px-3 py-2 border ${
                                    confirmStatus === 'declined' ? 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30 opacity-60' :
                                    assignment.payment_status === 'paid' 
                                      ? 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30' 
                                      : 'bg-gold/10 border-gold/20'
                                  }`}
                                  data-testid={`assignment-${assignment.assignment_id}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium">{assignment.crew_member_name}</span>
                                    <span className="text-xs text-muted-foreground">
                                      ({assignment.day_type.replace('_', ' ')})
                                    </span>
                                    {confirmStatus === 'accepted' && (
                                      <span className="text-xs px-1.5 py-0.5 bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400">Accepted</span>
                                    )}
                                    {confirmStatus === 'pending_confirmation' && (
                                      <span className="text-xs px-1.5 py-0.5 bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400">Awaiting</span>
                                    )}
                                    {confirmStatus === 'declined' && (
                                      <span className="text-xs px-1.5 py-0.5 bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400">Declined</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3">
                                    {isAdmin && (
                                      <>
                                        {assignment.payment_status === 'paid' ? (
                                          <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400">PAID</span>
                                        ) : (
                                          <button
                                            onClick={async () => {
                                              try {
                                                await assignmentAPI.updatePayment(assignment.assignment_id, 'paid');
                                                toast.success('Marked as paid!');
                                                const assignRes = await eventAPI.getAssignments(event.event_id);
                                                setEventAssignments(prev => ({...prev, [event.event_id]: assignRes.data}));
                                              } catch (err) {
                                                toast.error('Failed to update');
                                              }
                                            }}
                                            className="text-xs px-2 py-1 bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-200"
                                          >
                                            MARK PAID
                                          </button>
                                        )}
                                        {assignment.payment_status !== 'paid' && (
                                          <button
                                            onClick={() => { setAmountEditFor(assignment); setAmountEditEventId(event.event_id); }}
                                            className="text-muted-foreground hover:text-gold p-1"
                                            title="Edit amount"
                                            data-testid={`edit-amount-open-${assignment.assignment_id}`}
                                          >
                                            <Edit size={14} />
                                          </button>
                                        )}
                                        <button
                                          onClick={() => removeCrewAssignment(assignment.assignment_id, event.event_id)}
                                          className="text-red-500 hover:text-red-700 dark:text-red-400 p-1"
                                          title="Remove crew member"
                                        >
                                          <X size={14} />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No crew assigned yet</p>
                        )
                      ) : (
                        <p className="text-sm text-muted-foreground">Loading crew assignments...</p>
                      )}
                    </div>

                    {event.event_notes && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <p className="text-sm text-muted-foreground">{event.event_notes}</p>
                      </div>
                    )}

                    {/* SHOT LIST (collapsible) — hidden per admin request (backend intact) */}
                    {false && isAdmin && (
                      <EventShotListSection event={event} />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="card p-12 text-center">
                <p className="text-muted-foreground mb-4">No events yet for this project</p>
                {isAdmin && (
                  <button
                    onClick={handleCreateEvent}
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2"
                  >
                    <Plus size={16} />
                    ADD FIRST EVENT
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Files Section - Visible to all, upload/delete for admin only */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-serif">Project Files</h2>
              {isAdmin && (
                <label className={`flex items-center gap-2 px-4 py-2 border border-black/15 text-foreground hover:bg-black hover:text-white transition-colors text-xs cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  <Upload size={14} />
                  {uploading ? 'UPLOADING...' : 'UPLOAD FILE'}
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, selectedProject.project_id)}
                    disabled={uploading}
                    data-testid="file-upload-input"
                  />
                </label>
              )}
            </div>
            {projectFiles.length > 0 ? (
              <div className="space-y-2">
                {projectFiles.map(file => {
                  const Icon = getFileIcon(file.mime_type);
                  const sizeKB = file.size ? (file.size / 1024).toFixed(1) : '0';
                  const sizeDisplay = file.size > 1048576 ? `${(file.size / 1048576).toFixed(1)} MB` : `${sizeKB} KB`;
                  return (
                    <div key={file.file_id} className="flex items-center justify-between p-3 border border-border hover:border-gold/30 transition-colors" data-testid={`file-${file.file_id}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <Icon size={18} className="text-gold flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">{sizeDisplay} &bull; {formatDate(file.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        {file.web_view_link && (
                          <a href={file.web_view_link} target="_blank" rel="noopener noreferrer" className="p-1.5 hover:bg-accent transition-colors" title="Open in Google Drive">
                            <ExternalLink size={14} />
                          </a>
                        )}
                        {isAdmin && (
                          <button onClick={() => handleDeleteFile(file.file_id, selectedProject.project_id)} className="p-1.5 text-destructive hover:bg-destructive/10 transition-colors" title="Delete">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="card p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  {isAdmin ? 'No files yet. Upload files to store them on Google Drive.' : 'No files uploaded for this project yet.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Event Modal */}
        {amountEditFor && (
          <EditAmountModal
            assignment={amountEditFor}
            onClose={() => { setAmountEditFor(null); setAmountEditEventId(null); }}
            onSaved={async () => {
              if (amountEditEventId) {
                const r = await eventAPI.getAssignments(amountEditEventId);
                setEventAssignments(prev => ({ ...prev, [amountEditEventId]: r.data }));
              }
            }}
          />
        )}
        {showEventModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-background w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
              <h2 className="text-2xl font-serif mb-6">{editingEvent ? 'Edit Event' : 'Create Event'}</h2>
              <form onSubmit={handleEventSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Event Name</label>
                    <input
                      type="text"
                      value={eventFormData.event_name}
                      onChange={(e) => setEventFormData({...eventFormData, event_name: e.target.value})}
                      className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Event Type</label>
                    <select
                      value={eventFormData.event_type}
                      onChange={(e) => setEventFormData({...eventFormData, event_type: e.target.value})}
                      className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    >
                      {['Wedding', 'Wedding & Reception', 'Pre-wedding', 'Mehendi', 'Haldi', 'Sangeet', 'Reception', 'Engagement', 'Photoshoot', 'Vidhi', 'Baraat', 'Other'].map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Event Date</label>
                    <input
                      type="date"
                      value={eventFormData.event_date}
                      onChange={(e) => setEventFormData({...eventFormData, event_date: e.target.value})}
                      className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Start Time</label>
                    <input
                      type="text"
                      value={eventFormData.start_time}
                      onChange={(e) => setEventFormData({...eventFormData, start_time: e.target.value})}
                      className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                      placeholder="09:00 AM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">End Time</label>
                    <input
                      type="text"
                      value={eventFormData.end_time}
                      onChange={(e) => setEventFormData({...eventFormData, end_time: e.target.value})}
                      className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                      placeholder="05:00 PM"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Location</label>
                    <input
                      type="text"
                      value={eventFormData.location}
                      onChange={(e) => setEventFormData({...eventFormData, location: e.target.value})}
                      className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm mb-2 uppercase tracking-wide">Venue Address</label>
                    <input
                      type="text"
                      value={eventFormData.venue_address}
                      onChange={(e) => setEventFormData({...eventFormData, venue_address: e.target.value})}
                      className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm mb-2 uppercase tracking-wide">Event Notes</label>
                    <textarea
                      value={eventFormData.event_notes}
                      onChange={(e) => setEventFormData({...eventFormData, event_notes: e.target.value})}
                      className="w-full px-3 py-2 border border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                      rows="3"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Crew Needed for this Event</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs mb-1 text-muted-foreground">Photographers</label>
                        <input
                          type="number"
                          min="0"
                          value={eventFormData.required_photographers ?? 0}
                          onChange={(e) => setEventFormData({...eventFormData, required_photographers: parseInt(e.target.value) || 0})}
                          className="w-full px-3 py-2 border border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                          data-testid="req-photographers-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs mb-1 text-muted-foreground">Videographers</label>
                        <input
                          type="number"
                          min="0"
                          value={eventFormData.required_videographers ?? 0}
                          onChange={(e) => setEventFormData({...eventFormData, required_videographers: parseInt(e.target.value) || 0})}
                          className="w-full px-3 py-2 border border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                          data-testid="req-videographers-input"
                        />
                      </div>
                      <div>
                        <label className="block text-xs mb-1 text-muted-foreground">Cinematographers</label>
                        <input
                          type="number"
                          min="0"
                          value={eventFormData.required_cinematographers ?? 0}
                          onChange={(e) => setEventFormData({...eventFormData, required_cinematographers: parseInt(e.target.value) || 0})}
                          className="w-full px-3 py-2 border border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                          data-testid="req-cinematographers-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="submit" className="bg-primary text-primary-foreground px-6 py-3">
                    {editingEvent ? 'UPDATE' : 'CREATE'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowEventModal(false)}
                    className="border border-border px-6 py-3"
                  >
                    CANCEL
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Assignment Modal - Single Event */}
        {showAssignModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-background w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-medium mb-2">Assign Crew</h2>
                <p className="text-muted-foreground">
                  Assigning to: <span className="text-gold font-medium">{selectedEvent?.event_name}</span>
                </p>
                {selectedEvent?.event_date && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDate(selectedEvent.event_date)}
                    {selectedEvent.start_time && selectedEvent.end_time ? ` • ${selectedEvent.start_time}–${selectedEvent.end_time}` : ''}
                  </p>
                )}
              </div>
              
              <form onSubmit={handleAssignSubmit} className="space-y-6">
                {assignments.map((assignment, index) => (
                  <div key={index} className="border border-border p-6 bg-muted/30">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-gold">Crew Member #{index + 1}</span>
                      {assignments.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setAssignments(assignments.filter((_, i) => i !== index))}
                          className="text-red-500 text-sm hover:underline"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm mb-2 uppercase tracking-wide">Select Crew Member</label>
                        <select
                          value={assignment.crew_member_id}
                          onChange={(e) => {
                            const newAssignments = [...assignments];
                            newAssignments[index].crew_member_id = e.target.value;
                            setAssignments(newAssignments);
                          }}
                          className="w-full px-4 py-3 border border-input bg-background"
                          data-testid={`assign-crew-select-${index}`}
                        >
                          <option value="">-- Select --</option>
                          {crewMembers.map(crew => {
                            const av = availability[crew.crew_id];
                            let suffix = '';
                            if (av) {
                              if (av.status === 'blocked') suffix = ' — BLOCKED';
                              else if (av.status === 'busy') suffix = ` — Busy (${av.conflicts.length} event${av.conflicts.length > 1 ? 's' : ''})`;
                              else suffix = ' — Available';
                            }
                            return (
                              <option key={crew.crew_id} value={crew.crew_id}>
                                {crew.full_name} ({crew.role}){suffix}
                              </option>
                            );
                          })}
                        </select>
                        {assignment.crew_member_id && availability[assignment.crew_member_id] && (() => {
                          const av = availability[assignment.crew_member_id];
                          if (av.status === 'available') {
                            return (
                              <p className="mt-2 text-xs text-green-600 dark:text-green-400 flex items-center gap-1.5" data-testid={`availability-badge-${index}`}>
                                <span className="w-2 h-2 rounded-full bg-green-500" />
                                Available this day — no conflicts
                              </p>
                            );
                          }
                          if (av.status === 'blocked') {
                            return (
                              <div className="mt-2 p-2 border border-red-500/40 bg-red-500/10 text-xs" data-testid={`availability-badge-${index}`}>
                                <p className="flex items-center gap-1.5 text-red-600 dark:text-red-400 font-medium">
                                  <span className="w-2 h-2 rounded-full bg-red-500" />
                                  BLOCKED on {selectedEvent?.event_date}
                                </p>
                                {av.blocked_note && <p className="text-red-600 dark:text-red-400/80 mt-0.5">Note: {av.blocked_note}</p>}
                              </div>
                            );
                          }
                          return (
                            <div className="mt-2 p-2 border border-amber-500/40 bg-amber-500/10 text-xs" data-testid={`availability-badge-${index}`}>
                              <p className="flex items-center gap-1.5 text-amber-700 dark:text-amber-400 font-medium mb-1">
                                <span className="w-2 h-2 rounded-full bg-amber-500" />
                                Already assigned to {av.conflicts.length} event{av.conflicts.length > 1 ? 's' : ''} this day
                              </p>
                              <ul className="space-y-0.5 pl-4 text-amber-800 dark:text-amber-300/90">
                                {av.conflicts.map(c => (
                                  <li key={c.event_id}>
                                    • {c.event_name}
                                    {c.start_time && c.end_time ? ` (${c.start_time}–${c.end_time})` : ''}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })()}
                      </div>
                      
                      <div>
                        <label className="block text-sm mb-2 uppercase tracking-wide">Assignment Type</label>
                        <select
                          value={assignment.day_type}
                          onChange={(e) => {
                            const newAssignments = [...assignments];
                            newAssignments[index].day_type = e.target.value;
                            setAssignments(newAssignments);
                          }}
                          className="w-full px-4 py-3 border border-input bg-background"
                        >
                          <option value="half_day">Half Day</option>
                          <option value="full_day">Full Day</option>
                          <option value="custom">Custom Amount</option>
                        </select>
                      </div>
                    </div>

                    {/* Currency selector for Half Day / Full Day */}
                    {(assignment.day_type === 'half_day' || assignment.day_type === 'full_day') && (
                      <div className="mt-4">
                        <label className="block text-sm mb-2 uppercase tracking-wide">Payment Currency</label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`currency-event-${index}`}
                              value="USD"
                              checked={(assignment.currency || 'USD') === 'USD'}
                              onChange={(e) => {
                                const newAssignments = [...assignments];
                                newAssignments[index].currency = e.target.value;
                                setAssignments(newAssignments);
                              }}
                              className="w-4 h-4 accent-gold"
                            />
                            <span className="text-sm">USD ($)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`currency-event-${index}`}
                              value="INR"
                              checked={assignment.currency === 'INR'}
                              onChange={(e) => {
                                const newAssignments = [...assignments];
                                newAssignments[index].currency = e.target.value;
                                setAssignments(newAssignments);
                              }}
                              className="w-4 h-4 accent-gold"
                            />
                            <span className="text-sm">INR (₹)</span>
                          </label>
                        </div>
                      </div>
                    )}
                    
                    {assignment.day_type === 'custom' && (
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm mb-2 uppercase tracking-wide">Amount (USD)</label>
                          <input
                            type="number"
                            value={assignment.custom_amount_usd}
                            onChange={(e) => {
                              const newAssignments = [...assignments];
                              newAssignments[index].custom_amount_usd = parseFloat(e.target.value) || 0;
                              setAssignments(newAssignments);
                            }}
                            className="w-full px-4 py-3 border border-input bg-background"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label className="block text-sm mb-2 uppercase tracking-wide">Amount (INR)</label>
                          <input
                            type="number"
                            value={assignment.custom_amount_inr}
                            onChange={(e) => {
                              const newAssignments = [...assignments];
                              newAssignments[index].custom_amount_inr = parseFloat(e.target.value) || 0;
                              setAssignments(newAssignments);
                            }}
                            className="w-full px-4 py-3 border border-input bg-background"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={() => setAssignments([...assignments, {
                    crew_member_id: '',
                    day_type: 'full_day',
                    custom_amount_usd: 0,
                    custom_amount_inr: 0,
                    assigned_gear: []
                  }])}
                  className="w-full rounded-xl border border-dashed border-black/20 text-foreground px-4 py-3 hover:bg-secondary transition-colors"
                >
                  + ADD ANOTHER CREW MEMBER
                </button>
                
                <div className="flex gap-4 pt-4 border-t border-border">
                  <button type="submit" className="flex-1 bg-primary text-primary-foreground px-6 py-3 font-medium hover:bg-black/90 transition-colors">
                    ASSIGN CREW
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAssignModal(false)}
                    className="flex-1 border border-border px-6 py-3 hover:bg-accent transition-colors"
                  >
                    CANCEL
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Conflict Confirmation Modal */}
        {pendingConflicts && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4" data-testid="conflict-confirm-modal">
            <div className="bg-background w-full max-w-xl max-h-[85vh] overflow-y-auto">
              <div className="p-6 border-b border-border">
                <h3 className="text-xl font-medium text-amber-600 dark:text-amber-400 flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  Scheduling Conflicts Detected
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Some crew members are already busy or blocked on {selectedEvent?.event_date}. Review below before confirming.
                </p>
              </div>

              <div className="p-6 space-y-4">
                {pendingConflicts.map((c) => (
                  <div
                    key={c.crew_id}
                    className={`border p-4 text-sm ${c.blocked ? 'border-red-500/40 bg-red-500/5' : 'border-amber-500/40 bg-amber-500/5'}`}
                    data-testid={`conflict-row-${c.crew_id}`}
                  >
                    <p className="font-medium mb-1.5">{c.full_name}</p>
                    {c.blocked && (
                      <p className="text-red-600 dark:text-red-400 text-xs mb-1">
                        ⛔ BLOCKED{c.blocked_note ? ` — ${c.blocked_note}` : ''}
                      </p>
                    )}
                    {c.conflicts && c.conflicts.length > 0 && (
                      <ul className="space-y-1 pl-4 text-xs">
                        {c.conflicts.map(cf => (
                          <li key={cf.event_id} className={cf.time_overlap ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}>
                            • {cf.event_name}
                            {cf.start_time && cf.end_time ? ` (${cf.start_time}–${cf.end_time})` : ''}
                            {cf.time_overlap && <span className="ml-1 font-semibold">— TIME OVERLAP</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-3 p-6 border-t border-border">
                <button
                  type="button"
                  onClick={() => { setPendingConflicts(null); setPendingSubmitPayload(null); }}
                  className="flex-1 border border-border px-6 py-3 hover:bg-accent transition-colors text-sm"
                  data-testid="conflict-cancel-btn"
                >
                  GO BACK
                </button>
                <button
                  type="button"
                  onClick={() => performAssignmentSave(pendingSubmitPayload)}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 transition-colors text-sm font-medium"
                  data-testid="conflict-confirm-btn"
                >
                  ASSIGN ANYWAY
                </button>
              </div>
            </div>
          </div>
        )}
        {showProjectAssignModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-background w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-medium mb-2">Assign Crew to All Events</h2>
                <div className="bg-gold/10 border border-gold/20 p-4 mt-4">
                  <p className="text-sm">
                    <span className="font-medium text-gold">Note:</span> This will assign the selected crew members to 
                    <span className="font-medium"> ALL {projectEvents.length} events</span> in this project:
                  </p>
                  <ul className="mt-2 text-sm text-muted-foreground">
                    {projectEvents.slice(0, 5).map(event => (
                      <li key={event.event_id}>• {event.event_name} ({event.event_date})</li>
                    ))}
                    {projectEvents.length > 5 && (
                      <li>• ...and {projectEvents.length - 5} more events</li>
                    )}
                  </ul>
                </div>
              </div>
              
              <form onSubmit={handleAssignSubmit} className="space-y-6">
                {assignments.map((assignment, index) => (
                  <div key={index} className="border border-border p-6 bg-muted/30">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-gold">Crew Member #{index + 1}</span>
                      {assignments.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setAssignments(assignments.filter((_, i) => i !== index))}
                          className="text-red-500 text-sm hover:underline"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm mb-2 uppercase tracking-wide">Select Crew Member</label>
                        <select
                          value={assignment.crew_member_id}
                          onChange={(e) => {
                            const newAssignments = [...assignments];
                            newAssignments[index].crew_member_id = e.target.value;
                            setAssignments(newAssignments);
                          }}
                          className="w-full px-4 py-3 border border-input bg-background"
                        >
                          <option value="">-- Select --</option>
                          {crewMembers.map(crew => (
                            <option key={crew.crew_id} value={crew.crew_id}>
                              {crew.full_name} ({crew.role})
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm mb-2 uppercase tracking-wide">Assignment Type</label>
                        <select
                          value={assignment.day_type}
                          onChange={(e) => {
                            const newAssignments = [...assignments];
                            newAssignments[index].day_type = e.target.value;
                            setAssignments(newAssignments);
                          }}
                          className="w-full px-4 py-3 border border-input bg-background"
                        >
                          <option value="half_day">Half Day</option>
                          <option value="full_day">Full Day</option>
                          <option value="custom">Custom Amount</option>
                        </select>
                      </div>
                    </div>

                    {/* Currency selector for Half Day / Full Day - Project Assignment */}
                    {(assignment.day_type === 'half_day' || assignment.day_type === 'full_day') && (
                      <div className="mt-4">
                        <label className="block text-sm mb-2 uppercase tracking-wide">Payment Currency</label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`currency-project-${index}`}
                              value="USD"
                              checked={(assignment.currency || 'USD') === 'USD'}
                              onChange={(e) => {
                                const newAssignments = [...assignments];
                                newAssignments[index].currency = e.target.value;
                                setAssignments(newAssignments);
                              }}
                              className="w-4 h-4 accent-gold"
                            />
                            <span className="text-sm">USD ($)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name={`currency-project-${index}`}
                              value="INR"
                              checked={assignment.currency === 'INR'}
                              onChange={(e) => {
                                const newAssignments = [...assignments];
                                newAssignments[index].currency = e.target.value;
                                setAssignments(newAssignments);
                              }}
                              className="w-4 h-4 accent-gold"
                            />
                            <span className="text-sm">INR (₹)</span>
                          </label>
                        </div>
                      </div>
                    )}

                    {assignment.day_type === 'custom' && (
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm mb-2 uppercase tracking-wide">Amount (USD)</label>
                          <input
                            type="number"
                            value={assignment.custom_amount_usd}
                            onChange={(e) => {
                              const newAssignments = [...assignments];
                              newAssignments[index].custom_amount_usd = parseFloat(e.target.value) || 0;
                              setAssignments(newAssignments);
                            }}
                            className="w-full px-4 py-3 border border-input bg-background"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label className="block text-sm mb-2 uppercase tracking-wide">Amount (INR)</label>
                          <input
                            type="number"
                            value={assignment.custom_amount_inr}
                            onChange={(e) => {
                              const newAssignments = [...assignments];
                              newAssignments[index].custom_amount_inr = parseFloat(e.target.value) || 0;
                              setAssignments(newAssignments);
                            }}
                            className="w-full px-4 py-3 border border-input bg-background"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={() => setAssignments([...assignments, {
                    crew_member_id: '',
                    day_type: 'full_day',
                    currency: 'USD',
                    custom_amount_usd: 0,
                    custom_amount_inr: 0,
                    assigned_gear: []
                  }])}
                  className="w-full rounded-xl border border-dashed border-black/20 text-foreground px-4 py-3 hover:bg-secondary transition-colors"
                >
                  + ADD ANOTHER CREW MEMBER
                </button>
                
                <div className="flex gap-4 pt-4 border-t border-border">
                  <button type="submit" className="flex-1 bg-primary text-primary-foreground px-6 py-3 font-medium hover:bg-black/90 transition-colors">
                    ASSIGN TO ALL EVENTS
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowProjectAssignModal(false)}
                    className="flex-1 border border-border px-6 py-3 hover:bg-accent transition-colors"
                  >
                    CANCEL
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Notification Modal - Project Detail View */}
        {showNotificationModal && notificationEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-background w-full max-w-lg max-h-[90vh] overflow-y-auto p-8">
              <h2 className="text-2xl font-serif mb-2">Send Notification</h2>
              <p className="text-sm text-muted-foreground mb-6">
                Notify crew members about: <span className="text-gold">{notificationEvent.event_name}</span>
              </p>

              {/* Event Preview */}
              <div className="mb-6 p-4 bg-muted text-sm">
                <p><strong>Event:</strong> {notificationEvent.event_name} ({notificationEvent.event_type})</p>
                <p><strong>Date:</strong> {notificationEvent.event_date}</p>
                <p><strong>Time:</strong> {notificationEvent.start_time} - {notificationEvent.end_time}</p>
                <p><strong>Venue:</strong> {notificationEvent.venue_address}</p>
              </div>

              {/* Crew Members to Notify */}
              <div className="mb-6">
                <p className="text-sm font-medium mb-2">Crew Members to Notify:</p>
                <div className="space-y-2">
                  {eventAssignments[notificationEvent.event_id]?.map((assignment) => (
                    <label key={assignment.assignment_id} className="flex items-center gap-3 p-2 border border-border cursor-pointer hover:bg-accent">
                      <input
                        type="checkbox"
                        checked={selectedAssignmentsForNotification.includes(assignment.assignment_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAssignmentsForNotification([...selectedAssignmentsForNotification, assignment.assignment_id]);
                          } else {
                            setSelectedAssignmentsForNotification(selectedAssignmentsForNotification.filter(id => id !== assignment.assignment_id));
                          }
                        }}
                        className="w-4 h-4"
                      />
                      <div className="flex-1">
                        <p className="font-medium">{assignment.crew_member_name}</p>
                        <p className="text-xs text-muted-foreground">{assignment.crew_member_email} • {assignment.crew_member_phone}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Notification Options */}
              <div className="space-y-3">
                <p className="text-sm font-medium">Choose notification method:</p>
                
                <button
                  onClick={() => sendNotification('email')}
                  disabled={sendingNotification || !notificationConfig?.email?.configured}
                  className={`w-full flex items-center gap-3 p-4 border transition-colors ${
                    notificationConfig?.email?.configured
                      ? 'border-border hover:border-gold hover:bg-gold/5'
                      : 'border-border opacity-50 cursor-not-allowed'
                  }`}
                >
                  <Mail className="text-gold" size={24} />
                  <div className="text-left flex-1">
                    <p className="font-medium">Email</p>
                    <p className="text-xs text-muted-foreground">
                      {notificationConfig?.email?.configured 
                        ? `Send from ${notificationConfig.email.sender}` 
                        : 'Not configured - Add RESEND_API_KEY'}
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => sendNotification('sms')}
                  disabled={sendingNotification || !notificationConfig?.sms?.configured}
                  className={`w-full flex items-center gap-3 p-4 border transition-colors ${
                    notificationConfig?.sms?.configured
                      ? 'border-border hover:border-gold hover:bg-gold/5'
                      : 'border-border opacity-50 cursor-not-allowed'
                  }`}
                >
                  <Phone className="text-gold" size={24} />
                  <div className="text-left flex-1">
                    <p className="font-medium">SMS / Text</p>
                    <p className="text-xs text-muted-foreground">
                      {notificationConfig?.sms?.configured 
                        ? `Send from ${notificationConfig.sms.from_number}` 
                        : 'Not configured - Add Twilio credentials'}
                    </p>
                  </div>
                </button>

                <button
                  onClick={() => sendNotification('whatsapp')}
                  disabled={sendingNotification || !notificationConfig?.whatsapp?.configured}
                  className={`w-full flex items-center gap-3 p-4 border transition-colors ${
                    notificationConfig?.whatsapp?.configured
                      ? 'border-border hover:border-gold hover:bg-gold/5'
                      : 'border-border opacity-50 cursor-not-allowed'
                  }`}
                >
                  <MessageSquare className="text-gold" size={24} />
                  <div className="text-left flex-1">
                    <p className="font-medium">WhatsApp</p>
                    <p className="text-xs text-muted-foreground">
                      {notificationConfig?.whatsapp?.configured 
                        ? `Send from ${notificationConfig.whatsapp.from_number}` 
                        : 'Not configured - Add Twilio WhatsApp credentials'}
                    </p>
                  </div>
                </button>
              </div>

              {sendingNotification && (
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  Sending notifications...
                </div>
              )}

              <div className="mt-6 pt-4 border-t border-border">
                <button
                  onClick={() => setShowNotificationModal(false)}
                  className="w-full border border-border px-6 py-3 hover:bg-accent"
                >
                  CLOSE
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Project Edit Modal (in detail view) */}
        {showProjectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-background w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
              <h2 className="text-2xl font-serif mb-6">{editingProject ? 'Edit Project' : 'Create Project'}</h2>
              <form onSubmit={handleProjectSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm mb-2 uppercase tracking-wide">Project Name</label>
                    <input type="text" value={projectFormData.project_name || ''} onChange={(e) => setProjectFormData({...projectFormData, project_name: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" required placeholder="Kumar Wedding" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Client Name</label>
                    <input type="text" value={projectFormData.client_name || ''} onChange={(e) => setProjectFormData({...projectFormData, client_name: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" required />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Bride Name</label>
                    <input type="text" value={projectFormData.bride_name || ''} onChange={(e) => setProjectFormData({...projectFormData, bride_name: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Groom Name</label>
                    <input type="text" value={projectFormData.groom_name || ''} onChange={(e) => setProjectFormData({...projectFormData, groom_name: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Bride Instagram</label>
                    <input
                      type="text"
                      value={projectFormData.bride_instagram || ''}
                      onChange={(e) => setProjectFormData({...projectFormData, bride_instagram: e.target.value})}
                      className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                      placeholder="@bride_handle"
                      data-testid="project-bride-instagram"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Groom Instagram</label>
                    <input
                      type="text"
                      value={projectFormData.groom_instagram || ''}
                      onChange={(e) => setProjectFormData({...projectFormData, groom_instagram: e.target.value})}
                      className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                      placeholder="@groom_handle"
                      data-testid="project-groom-instagram"
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Client Email</label>
                    <input type="email" value={projectFormData.client_email || ''} onChange={(e) => setProjectFormData({...projectFormData, client_email: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" required />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Client Phone</label>
                    <input type="tel" value={projectFormData.client_phone || ''} onChange={(e) => setProjectFormData({...projectFormData, client_phone: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" required placeholder="+1234567890" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Secondary Contact Name</label>
                    <input type="text" value={projectFormData.secondary_contact_name || ''} onChange={(e) => setProjectFormData({...projectFormData, secondary_contact_name: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Secondary Contact Phone</label>
                    <input type="tel" value={projectFormData.secondary_contact_phone || ''} onChange={(e) => setProjectFormData({...projectFormData, secondary_contact_phone: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Project Start Date</label>
                    <input type="date" value={projectFormData.project_start_date || ''} onChange={(e) => setProjectFormData({...projectFormData, project_start_date: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" required />
                  </div>
                  <div>
                    <label className="block text-sm mb-2 uppercase tracking-wide">Project End Date</label>
                    <input type="date" value={projectFormData.project_end_date || ''} onChange={(e) => setProjectFormData({...projectFormData, project_end_date: e.target.value})} className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground" required />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm mb-2 uppercase tracking-wide">Project Notes</label>
                    <textarea value={projectFormData.notes || ''} onChange={(e) => setProjectFormData({...projectFormData, notes: e.target.value})} className="w-full px-3 py-2 border border-input bg-transparent text-foreground placeholder:text-muted-foreground" rows="3" />
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="submit" className="bg-primary text-primary-foreground px-6 py-3">{editingProject ? 'UPDATE' : 'CREATE'}</button>
                  <button type="button" onClick={() => setShowProjectModal(false)} className="border border-border px-6 py-3">CANCEL</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Time change notification modal (detail view) */}
        {timeChangeInfo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="time-change-modal">
            <div className="bg-background w-full max-w-md p-6">
              <h3 className="text-xl font-serif mb-2">Notify Crew of Time Change?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                You changed the timing for <strong>{timeChangeInfo.event_name}</strong>.
                {timeChangeInfo.google_synced && (
                  <span className="block text-xs mt-1 text-emerald-600 dark:text-emerald-400">✓ Google Calendar re-synced automatically</span>
                )}
              </p>
              <div className="bg-muted/30 p-3 mb-4 text-xs space-y-1">
                <div>
                  <span className="text-muted-foreground">Previous: </span>
                  <span className="line-through">
                    {timeChangeInfo.old_values?.event_date} {timeChangeInfo.old_values?.start_time}–{timeChangeInfo.old_values?.end_time}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">New: </span>
                  <span className="font-medium text-emerald-700 dark:text-emerald-400">
                    {timeChangeInfo.new_values?.event_date} {timeChangeInfo.new_values?.start_time}–{timeChangeInfo.new_values?.end_time}
                  </span>
                </div>
              </div>
              <p className="text-sm mb-4">
                <strong>{timeChangeInfo.crew_count}</strong> assigned crew member{timeChangeInfo.crew_count !== 1 ? 's' : ''} will be notified.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => sendTimeChangeNotifications(['email', 'sms'])}
                  className="w-full bg-primary text-primary-foreground px-4 py-2.5 text-sm hover:bg-primary/90"
                  data-testid="notify-email-sms"
                >
                  SEND EMAIL + SMS
                </button>
                <button
                  onClick={() => sendTimeChangeNotifications(['email'])}
                  className="w-full border border-border px-4 py-2.5 text-sm hover:bg-accent"
                  data-testid="notify-email-only"
                >
                  EMAIL ONLY
                </button>
                <button
                  onClick={() => setTimeChangeInfo(null)}
                  className="w-full text-muted-foreground px-4 py-2 text-sm hover:text-foreground"
                  data-testid="notify-skip"
                >
                  SKIP — DON'T NOTIFY
                </button>
              </div>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="projects-page">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-serif mb-1 sm:mb-2">Projects</h1>
              <p className="text-sm text-muted-foreground">Manage client projects and events</p>
            </div>
            {isAdmin && (
              <button
                onClick={handleCreateProject}
                className="flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 sm:px-6 py-2.5 sm:py-3 hover:bg-primary/90 text-sm"
                data-testid="create-project-button"
              >
                <Plus size={16} />
                CREATE PROJECT
              </button>
            )}
          </div>
        </div>

        {/* Search bar */}
        <div className="relative mb-4 sm:mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <input
            type="text"
            placeholder="Search projects by name, bride, or groom…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-9 py-2.5 border border-input bg-background text-sm focus:outline-none focus:border-gold"
            data-testid="project-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
              data-testid="clear-search-btn"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Active / Archived Tabs + Year Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
          <div className="flex gap-1 border-b border-border">
            <button
              onClick={() => setProjectTab('active')}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm uppercase tracking-wide font-medium transition-colors ${
                projectTab === 'active' ? 'border-b-2 border-black text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-active-projects"
            >
              Active
            </button>
            <button
              onClick={() => setProjectTab('archived')}
              className={`px-4 sm:px-6 py-2.5 sm:py-3 text-xs sm:text-sm uppercase tracking-wide font-medium transition-colors ${
                projectTab === 'archived' ? 'border-b-2 border-black text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid="tab-archived-projects"
            >
              Archived
            </button>
          </div>
          
          {/* Year Filter */}
          {getAvailableYears().length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Year:</span>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-3 py-2 border border-input bg-background text-sm"
                data-testid="year-filter"
              >
                <option value="all">All Years</option>
                {getAvailableYears().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Projects grouped by year */}
        <div className="space-y-6">
          {sortedYears.length > 0 ? (
            sortedYears.map(year => (
              <div key={year} className="border border-border" data-testid={`year-group-${year}`}>
                {/* Year Header */}
                <button
                  onClick={() => toggleYearCollapse(year)}
                  className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-serif">{year}</span>
                    <span className="text-sm text-muted-foreground">
                      ({projectsByYear[year].length} project{projectsByYear[year].length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  {collapsedYears[year] ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                </button>
                
                {/* Projects in this year */}
                {!collapsedYears[year] && (
                  <div className="divide-y divide-border">
                    {projectsByYear[year].map((project) => (
                      <div 
                        key={project.project_id} 
                        className="p-4 sm:p-6 cursor-pointer hover:bg-accent/30 transition-colors"
                        onClick={() => handleViewProject(project)}
                        data-testid={`project-${project.project_id}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1 sm:mb-2">
                              <h3 className="text-lg sm:text-xl font-serif">
                                {project.bride_name && project.groom_name 
                                  ? `${project.bride_name} & ${project.groom_name}`
                                  : project.project_name}
                              </h3>
                              {(() => {
                                // "IN PROGRESS" pill: today is within [project_start_date, project_end_date]
                                const start = project.project_start_date;
                                const end = project.project_end_date || start;
                                if (start && end && start <= todayISO && todayISO <= end) {
                                  return (
                                    <span
                                      className="inline-flex items-center gap-1 text-[10px] sm:text-xs px-2 py-0.5 border uppercase tracking-wide bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900"
                                      data-testid={`in-progress-${project.project_id}`}
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                      In Progress
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                              {(() => {
                                const urg = getUrgency(project);
                                if (!urg) return null;
                                const cls = urg.color === 'red'
                                  ? 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900'
                                  : 'bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900';
                                const icon = urg.color === 'red' ? '⚠' : '⏰';
                                return (
                                  <span
                                    className={`inline-flex items-center gap-1 text-[10px] sm:text-xs px-2 py-0.5 border uppercase tracking-wide ${cls}`}
                                    data-testid={`urgency-${project.project_id}`}
                                  >
                                    {icon} {urg.label}
                                  </span>
                                );
                              })()}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-muted-foreground">
                              <div className="flex items-center gap-1.5">
                                <CalendarIcon size={14} />
                                <span>{formatDate(project.project_start_date)}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <CalendarIcon size={14} />
                                <span>{project.event_count || 0} Events</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            {isAdmin && (
                              <>
                                {projectTab === 'active' ? (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleArchiveProject(project.project_id);
                                      }}
                                      className="p-1.5 sm:p-2 border border-border hover:border-gold transition-colors"
                                      title="Archive Project"
                                      data-testid={`archive-project-${project.project_id}`}
                                    >
                                      <Archive size={14} />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditProject(project);
                                      }}
                                      className="p-1.5 sm:p-2 border border-border hover:border-gold transition-colors"
                                    >
                                      <Edit size={14} />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteProject(project.project_id);
                                      }}
                                      className="p-1.5 sm:p-2 border border-destructive text-destructive hover:bg-destructive/10"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleUnarchiveProject(project.project_id);
                                      }}
                                      className="p-2 border border-green-300 text-green-600 dark:text-green-400 hover:bg-green-50 dark:bg-green-500/10 transition-colors"
                                      title="Restore Project"
                                      data-testid={`unarchive-project-${project.project_id}`}
                                    >
                                      <ArchiveRestore size={16} />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteProject(project.project_id);
                                      }}
                                      className="p-2 border border-destructive text-destructive hover:bg-destructive/10"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                            <ChevronRight size={20} className="text-muted-foreground" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="card p-12 text-center">
              <p className="text-muted-foreground mb-4">
                {searchTerm
                  ? `No projects match "${searchQuery}"`
                  : (projectTab === 'active' ? 'No active projects. Create your first project!' : 'No archived projects.')}
              </p>
              {isAdmin && (
                <button
                  onClick={handleCreateProject}
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2"
                >
                  <Plus size={16} />
                  CREATE PROJECT
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Project Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-background w-full max-w-3xl max-h-[90vh] overflow-y-auto p-8">
            <h2 className="text-2xl font-serif mb-6">{editingProject ? 'Edit Project' : 'Create Project'}</h2>
            <form onSubmit={handleProjectSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm mb-2 uppercase tracking-wide">Project Name</label>
                  <input
                    type="text"
                    value={projectFormData.project_name}
                    onChange={(e) => setProjectFormData({...projectFormData, project_name: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    required
                    placeholder="Kumar Wedding"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Client Name</label>
                  <input
                    type="text"
                    value={projectFormData.client_name}
                    onChange={(e) => setProjectFormData({...projectFormData, client_name: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Bride Name</label>
                  <input
                    type="text"
                    value={projectFormData.bride_name}
                    onChange={(e) => setProjectFormData({...projectFormData, bride_name: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Groom Name</label>
                  <input
                    type="text"
                    value={projectFormData.groom_name}
                    onChange={(e) => setProjectFormData({...projectFormData, groom_name: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Client Email</label>
                  <input
                    type="email"
                    value={projectFormData.client_email}
                    onChange={(e) => setProjectFormData({...projectFormData, client_email: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Client Phone</label>
                  <input
                    type="tel"
                    value={projectFormData.client_phone}
                    onChange={(e) => setProjectFormData({...projectFormData, client_phone: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    required
                    placeholder="+1234567890"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Secondary Contact Name</label>
                  <input
                    type="text"
                    value={projectFormData.secondary_contact_name}
                    onChange={(e) => setProjectFormData({...projectFormData, secondary_contact_name: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Secondary Contact Phone</label>
                  <input
                    type="tel"
                    value={projectFormData.secondary_contact_phone}
                    onChange={(e) => setProjectFormData({...projectFormData, secondary_contact_phone: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Project Start Date</label>
                  <input
                    type="date"
                    value={projectFormData.project_start_date}
                    onChange={(e) => setProjectFormData({...projectFormData, project_start_date: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm mb-2 uppercase tracking-wide">Project End Date</label>
                  <input
                    type="date"
                    value={projectFormData.project_end_date}
                    onChange={(e) => setProjectFormData({...projectFormData, project_end_date: e.target.value})}
                    className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm mb-2 uppercase tracking-wide">Project Notes</label>
                  <textarea
                    value={projectFormData.notes}
                    onChange={(e) => setProjectFormData({...projectFormData, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                    rows="3"
                  />
                </div>
              </div>
              <div className="flex gap-4 pt-4">
                <button type="submit" className="bg-primary text-primary-foreground px-6 py-3">
                  {editingProject ? 'UPDATE' : 'CREATE'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowProjectModal(false)}
                  className="border border-border px-6 py-3"
                >
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      {showNotificationModal && notificationEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-background w-full max-w-lg max-h-[90vh] overflow-y-auto p-8">
            <h2 className="text-2xl font-serif mb-2">Send Notification</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Notify crew members about: <span className="text-gold">{notificationEvent.event_name}</span>
            </p>

            {/* Event Preview */}
            <div className="mb-6 p-4 bg-muted text-sm">
              <p><strong>Event:</strong> {notificationEvent.event_name} ({notificationEvent.event_type})</p>
              <p><strong>Date:</strong> {notificationEvent.event_date}</p>
              <p><strong>Time:</strong> {notificationEvent.start_time} - {notificationEvent.end_time}</p>
              <p><strong>Venue:</strong> {notificationEvent.venue_address}</p>
            </div>

            {/* Crew Members to Notify */}
            <div className="mb-6">
              <p className="text-sm font-medium mb-2">Crew Members to Notify:</p>
              <div className="space-y-2">
                {eventAssignments[notificationEvent.event_id]?.map((assignment) => (
                  <label key={assignment.assignment_id} className="flex items-center gap-3 p-2 border border-border cursor-pointer hover:bg-accent">
                    <input
                      type="checkbox"
                      checked={selectedAssignmentsForNotification.includes(assignment.assignment_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedAssignmentsForNotification([...selectedAssignmentsForNotification, assignment.assignment_id]);
                        } else {
                          setSelectedAssignmentsForNotification(selectedAssignmentsForNotification.filter(id => id !== assignment.assignment_id));
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{assignment.crew_member_name}</p>
                      <p className="text-xs text-muted-foreground">{assignment.crew_member_email} • {assignment.crew_member_phone}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Notification Options */}
            <div className="space-y-3">
              <p className="text-sm font-medium">Choose notification method:</p>
              
              <button
                onClick={() => sendNotification('email')}
                disabled={sendingNotification || !notificationConfig?.email?.configured}
                className={`w-full flex items-center gap-3 p-4 border transition-colors ${
                  notificationConfig?.email?.configured
                    ? 'border-border hover:border-gold hover:bg-gold/5'
                    : 'border-border opacity-50 cursor-not-allowed'
                }`}
              >
                <Mail className="text-gold" size={24} />
                <div className="text-left flex-1">
                  <p className="font-medium">Email</p>
                  <p className="text-xs text-muted-foreground">
                    {notificationConfig?.email?.configured 
                      ? `Send from ${notificationConfig.email.sender}` 
                      : 'Not configured - Add RESEND_API_KEY'}
                  </p>
                </div>
              </button>

              <button
                onClick={() => sendNotification('sms')}
                disabled={sendingNotification || !notificationConfig?.sms?.configured}
                className={`w-full flex items-center gap-3 p-4 border transition-colors ${
                  notificationConfig?.sms?.configured
                    ? 'border-border hover:border-gold hover:bg-gold/5'
                    : 'border-border opacity-50 cursor-not-allowed'
                }`}
              >
                <Phone className="text-gold" size={24} />
                <div className="text-left flex-1">
                  <p className="font-medium">SMS / Text</p>
                  <p className="text-xs text-muted-foreground">
                    {notificationConfig?.sms?.configured 
                      ? `Send from ${notificationConfig.sms.from_number}` 
                      : 'Not configured - Add Twilio credentials'}
                  </p>
                </div>
              </button>

              <button
                onClick={() => sendNotification('whatsapp')}
                disabled={sendingNotification || !notificationConfig?.whatsapp?.configured}
                className={`w-full flex items-center gap-3 p-4 border transition-colors ${
                  notificationConfig?.whatsapp?.configured
                    ? 'border-border hover:border-gold hover:bg-gold/5'
                    : 'border-border opacity-50 cursor-not-allowed'
                }`}
              >
                <MessageSquare className="text-gold" size={24} />
                <div className="text-left flex-1">
                  <p className="font-medium">WhatsApp</p>
                  <p className="text-xs text-muted-foreground">
                    {notificationConfig?.whatsapp?.configured 
                      ? `Send from ${notificationConfig.whatsapp.from_number}` 
                      : 'Not configured - Add Twilio WhatsApp credentials'}
                  </p>
                </div>
              </button>
            </div>

            {sendingNotification && (
              <div className="mt-4 text-center text-sm text-muted-foreground">
                Sending notifications...
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-border">
              <button
                onClick={() => setShowNotificationModal(false)}
                className="w-full border border-border px-6 py-3 hover:bg-accent"
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time change notification modal */}
      {timeChangeInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="time-change-modal">
          <div className="bg-background w-full max-w-md p-6">
            <h3 className="text-xl font-serif mb-2">Notify Crew of Time Change?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              You changed the timing for <strong>{timeChangeInfo.event_name}</strong>.
              {timeChangeInfo.google_synced && (
                <span className="block text-xs mt-1 text-emerald-600 dark:text-emerald-400">✓ Google Calendar re-synced automatically</span>
              )}
            </p>
            <div className="bg-muted/30 p-3 mb-4 text-xs space-y-1">
              <div>
                <span className="text-muted-foreground">Previous: </span>
                <span className="line-through">
                  {timeChangeInfo.old_values?.event_date} {timeChangeInfo.old_values?.start_time}–{timeChangeInfo.old_values?.end_time}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">New: </span>
                <span className="font-medium text-emerald-700 dark:text-emerald-400">
                  {timeChangeInfo.new_values?.event_date} {timeChangeInfo.new_values?.start_time}–{timeChangeInfo.new_values?.end_time}
                </span>
              </div>
            </div>
            <p className="text-sm mb-4">
              <strong>{timeChangeInfo.crew_count}</strong> assigned crew member{timeChangeInfo.crew_count !== 1 ? 's' : ''} will be notified.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => sendTimeChangeNotifications(['email', 'sms'])}
                className="w-full bg-primary text-primary-foreground px-4 py-2.5 text-sm hover:bg-primary/90"
                data-testid="notify-email-sms"
              >
                SEND EMAIL + SMS
              </button>
              <button
                onClick={() => sendTimeChangeNotifications(['email'])}
                className="w-full border border-border px-4 py-2.5 text-sm hover:bg-accent"
                data-testid="notify-email-only"
              >
                EMAIL ONLY
              </button>
              <button
                onClick={() => setTimeChangeInfo(null)}
                className="w-full text-muted-foreground px-4 py-2 text-sm hover:text-foreground"
                data-testid="notify-skip"
              >
                SKIP — DON'T NOTIFY
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const EventShotListSection = ({ event }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid={`event-shotlist-${event.event_id}`}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 text-sm font-medium hover:text-gold transition-colors"
        data-testid={`event-shotlist-toggle-${event.event_id}`}
      >
        <CameraIcon size={16} className="text-gold" />
        Shot List
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="mt-3">
          <ShotListAdmin eventId={event.event_id} eventName={event.event_name} />
        </div>
      )}
    </div>
  );
};

export default Projects;
